import React, { useEffect } from 'react'
import {
  CONFIGURACAO_KANBAN,
  InvalidsRegisterPipelines,
  RegisterPipelines
} from 'store/modules/backoffice/configuracaoKanban/types'
import * as configuracaoKanbanActions from 'store/modules/backoffice/configuracaoKanban/actions'
import useRedux from 'hooks/useRedux'
import { useSelector } from 'react-redux'
import { ApplicationState } from 'store'
import SimpleModal from 'components/SimpleModal'
import { Col, Label, Row } from 'reactstrap'
import InputCustom from 'components/Inputs'
import { ButtonConfigKanban } from '../../style'
import { SimpleTitleCard } from 'styles/global'
import { maskNumber } from 'util/masks'
import { schemaPipelines } from '../../constants/schemas'
import * as yup from 'yup'
import { toast } from 'react-toastify'
import CustomSelect from 'components/Select'
import { Switch, Tooltip } from '@mui/material'

interface Props {
  isOpen: boolean
  toggle?: () => void
}

const ModalRegister = ({ isOpen, toggle }: Props) => {
  const { tabPipelines } = useSelector<ApplicationState, CONFIGURACAO_KANBAN>(
    (state) => state.configuracaoKanban
  )

  const { dispatch } = useRedux()

  useEffect(() => {
    if (isOpen && tabPipelines.pipelineSelecionado) {
      dispatch(
        configuracaoKanbanActions.getPipelineByIdTabPipelinesSaga(
          tabPipelines.pipelineSelecionado
        )
      )
    }
  }, [dispatch, isOpen, tabPipelines.pipelineSelecionado])

  useEffect(() => {
    if (isOpen) {
      dispatch(configuracaoKanbanActions.getCentrosCustoTabPipelinesSaga())
    }
  }, [dispatch, isOpen])

  function onChange<T extends keyof RegisterPipelines>(
    value: RegisterPipelines[T],
    field: T
  ) {
    dispatch(
      configuracaoKanbanActions.setRegisterFieldsTabPipelines(value, field)
    )
  }

  function onSave() {
    dispatch(configuracaoKanbanActions.cleanInvalidsRegisterTabPipelines())
    schemaPipelines
      .validate(tabPipelines.register, { abortEarly: false })
      .then(() => {
        if (tabPipelines.register?.id) {
          dispatch(configuracaoKanbanActions.editarPipelineTabPipelinesSaga())
        } else {
          dispatch(configuracaoKanbanActions.salvarPipelineTabPipelinesSaga())
        }
        toggle?.()
      })
      .catch((err: yup.ValidationError) => {
        err.inner.forEach((error) => {
          dispatch(
            configuracaoKanbanActions.setInvalidsFieldsRegisterTabPipelines(
              {
                invalid: true,
                message: error.message
              },
              error.path as keyof InvalidsRegisterPipelines
            )
          )
        })
        toast.warning('Por favor, verifique os campos inválidos.')
      })
  }

  return (
    <SimpleModal
      size="lg"
      isOpen={isOpen}
      toggle={() => {
        toggle?.()
      }}
      header={
        <SimpleTitleCard>
          {tabPipelines.register?.id
            ? 'Editar Pipeline'
            : 'Criar novo Pipeline'}
        </SimpleTitleCard>
      }
      footer={
        <div className="d-flex gap-3 justify-content-end">
          <ButtonConfigKanban
            className="btn-light"
            onClick={() => {
              toggle?.()
            }}
            disabled={tabPipelines.visualizacao}
          >
            Cancelar
          </ButtonConfigKanban>
          <ButtonConfigKanban
            className="btn-blue"
            onClick={onSave}
            disabled={tabPipelines.visualizacao}
          >
            {tabPipelines.register?.id ? 'Editar' : 'Criar'} Pipeline
          </ButtonConfigKanban>
        </div>
      }
    >
      <Row className="d-flex flex-column gap-4">
        <Col md={12}>
          <Label className="label-12">Nome do Pipeline</Label>
          <InputCustom
            type="text"
            placeholder="Ex: Pipeline de Vendas B2B"
            onChange={(e) => onChange(e.target.value, 'nomePipeline')}
            value={tabPipelines.register?.nomePipeline || ''}
            invalid={tabPipelines.invalids.nomePipeline.invalid}
            message={tabPipelines.invalids.nomePipeline.message}
            disabled={tabPipelines.visualizacao}
          />
        </Col>
        <Col md={12}>
          <Label className="label-12">Descrição</Label>
          <InputCustom
            type="text"
            placeholder="Descreva o propósito deste pipeline..."
            onChange={(e) => onChange(e.target.value, 'descricao')}
            value={tabPipelines.register?.descricao || ''}
            invalid={tabPipelines.invalids.descricao.invalid}
            message={tabPipelines.invalids.descricao.message}
            disabled={tabPipelines.visualizacao}
          />
        </Col>
        <Col md={12}>
          <Label className="label-12">Centro de Custo</Label>
          <CustomSelect
            options={tabPipelines.optionsSelect.centrosCusto}
            accessorLabel="descricao"
            accessorValue="id"
            onChange={(e) => onChange(e, 'centroCustoId')}
            value={tabPipelines.register?.centroCustoId || ''}
            invalid={tabPipelines.invalids.centroCustoId.invalid}
            message={tabPipelines.invalids.centroCustoId.message}
            disabled={tabPipelines.visualizacao}
          />
        </Col>
        <Col md={12}>
          <Label className="label-12">Máximo de Leads</Label>
          <InputCustom
            type="text"
            placeholder="Ex: 100"
            onChange={(e) =>
              onChange(Number(maskNumber(e.target.value)), 'quantidadeLeads')
            }
            value={tabPipelines.register?.quantidadeLeads || ''}
            invalid={tabPipelines.invalids.quantidadeLeads?.invalid || false}
            message={tabPipelines.invalids.quantidadeLeads?.message}
            disabled={tabPipelines.visualizacao}
          />
        </Col>
        <Col className="d-flex flex-column" md={12}>
          <div className="d-flex align-items-center mb-1">
            <Label className="label-12 mb-0">Site</Label>
            <Switch
              onChange={(e) => onChange(e.target.checked, 'site')}
              checked={tabPipelines.register?.site || false}
              disabled={tabPipelines.visualizacao}
            />
          </div>
          {tabPipelines.invalids.site.invalid && (
            <Tooltip
              title={tabPipelines.invalids.site.message}
              placement="bottom-start"
            >
              <span className="error-message">
                {tabPipelines.invalids.site.message}
              </span>
            </Tooltip>
          )}
        </Col>
      </Row>
    </SimpleModal>
  )
}

export default ModalRegister
